import type { StandardSchemaV1 } from '@standard-schema/spec'
import { electricCollectionOptions } from '@tanstack/electric-db-collection'
import { queryCollectionOptions } from '@tanstack/query-db-collection'
import type { Collection, InferSchemaOutput } from '@tanstack/vue-db'
import { createCollection } from '@tanstack/vue-db'
import { hasInjectionContext } from 'vue'
import { useRhythmicLogger, useRuntimeConfig } from '#imports'
import type { CollectionFactoryContext } from '../CollectionRegistry'
import { createClerkAuthorizationHeaderGetter } from './CollectionBuilder.auth'
import { buildCollectionId, buildWhereClause, ensurePartitionKey } from './CollectionBuilder.helpers'
import { createDefaultHandlers } from './CollectionBuilder.mutators'
import type { CollectionBuildRequestConfig } from './CollectionBuilder.types'

const logger = useRhythmicLogger('collection-builder')

type ClerkTokenGetter = (options?: { template?: string }) => Promise<string | null>

function sleep(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms))
}

async function resolveGlobalClerkToken(options?: { template?: string }) {
  if (!import.meta.client) return null

  const start = Date.now()
  while (Date.now() - start < 1500) {
    const clerk = (globalThis as Record<string, any> | undefined)?.Clerk
    if (clerk) {
      if (clerk.loaded === false && typeof clerk.load === 'function') {
        try {
          await clerk.load()
        } catch {
          // Ignore and retry
        }
      }

      const sessionGetter = clerk?.session?.getToken as ClerkTokenGetter | undefined
      if (typeof sessionGetter === 'function') {
        const token = await sessionGetter(options)
        if (token) return token
      }
    }

    await sleep(50)
  }

  return null
}

function resolveInjectedTokenGetter(): ClerkTokenGetter | null {
  if (!import.meta.client) return null
  if (!hasInjectionContext()) return null
  return null
}

interface ShapeClientOptions {
  headers?: Record<string, string | (() => Promise<string>)>
}

function resolveClientShapeOptions(): ShapeClientOptions | undefined {
  if (!import.meta.client) return undefined

  const runtimeConfig = useRuntimeConfig()
  const rawTemplate = runtimeConfig.public?.clerkJwtTemplate
  const tokenTemplate: string | undefined = typeof rawTemplate === 'string' ? rawTemplate : undefined
  const injectedTokenGetter = resolveInjectedTokenGetter()
  const authorizationHeaderGetter = createClerkAuthorizationHeaderGetter({
    tokenTemplate,
    injectedTokenGetter,
    sessionTokenGetter: resolveGlobalClerkToken,
  })

  return {
    headers: {
      Authorization: async () => {
        try {
          return await authorizationHeaderGetter()
        } catch (error) {
          logger.error('Failed to resolve auth token for shape request', {
            error,
          })
          throw error
        }
      },
    },
  }
}

export function buildCollection(context: CollectionFactoryContext, config: CollectionBuildRequestConfig) {
  ensurePartitionKey(config)
  const id = buildCollectionId(config)
  const defaultHandlers = createDefaultHandlers(config)

  type TSchema = typeof config.schema extends StandardSchemaV1
    ? InferSchemaOutput<typeof config.schema>
    : Record<string, unknown>
  type TItem = InferSchemaOutput<TSchema> & Record<string, unknown>
  type TKey = string | number

  if (config.collectionType === 'electric') {
    // build query by default
    try {
      const baseParams = {
        table: config.table,
        where: buildWhereClause(config.where),
        ...config.params,
      }
      let hasRetried = false
      const clientShapeOptions = resolveClientShapeOptions()
      const retryWithRefresh = async () => {
        const cacheBuster = `${Date.now()}-${Math.random().toString(16).slice(2)}`
        const url = new URL(context.shapeUrl)
        const params = {
          ...baseParams,
          offset: -1,
          cache_buster: cacheBuster,
        }
        Object.entries(params).forEach(([key, value]) => {
          if (value == null) return
          url.searchParams.set(key, String(value))
        })

        const authHeader = clientShapeOptions?.headers?.Authorization
        const headers: Record<string, string> = {}
        if (typeof authHeader === 'string') headers.Authorization = authHeader
        if (typeof authHeader === 'function') headers.Authorization = await authHeader()

        try {
          await fetch(url.toString(), { headers, method: 'GET' })
          logger.info('Retried Electric shape with cache_buster and offset=-1 after expired handle', {
            table: config.table,
          })
        } catch (error) {
          logger.error('Retry fetch for Electric shape failed', {
            error,
            table: config.table,
          })
        }
      }

      const shapeOnError = (error: unknown) => {
        const status = (error as any)?.status ?? (error as any)?.response?.status
        if ((status === 404 || status === 409) && !hasRetried) {
          hasRetried = true
          retryWithRefresh()
          return
        }

        logger.error('Electric shape subscription failed', { error })
        throw error instanceof Error ? error : new Error('Shape subscription failed; see server logs for details')
      }

      const shapeOptions = {
        url: context.shapeUrl,
        params: baseParams,
        liveSse: true,
        ...(clientShapeOptions ?? {}),
        onError: shapeOnError,
      }
      const options = {
        id,
        getKey: (item: any) => item[config.key as keyof typeof item] as string | number,
        shapeOptions,
        onInsert: (config.mutators?.insert ?? defaultHandlers.onInsert) as any,
        onUpdate: (config.mutators?.update ?? defaultHandlers.onUpdate) as any,
        onDelete: (config.mutators?.delete ?? defaultHandlers.onDelete) as any,
        schema: config.schema,
      }

      const configOptions = electricCollectionOptions(options)
      return createCollection(configOptions) as unknown as Collection<TItem, TKey>
    } catch (error) {
      logger.error('Failed to build electric collection', {
        table: config.table,
        error,
      })
      throw error
    }
  } else {
    try {
      const queryConfig = {
        queryClient: context.queryClient,
        queryFn: (config.queryFn ?? defaultHandlers.queryFn) as any,
        queryKey: [id] as any,
        getKey: (item: TItem) => item[config.key] as string | number,
        id,
        ...(config.schema ? { schema: config.schema } : {}),
        onInsert: (config.mutators?.insert ?? defaultHandlers.onInsert) as any,
        onUpdate: (config.mutators?.update ?? defaultHandlers.onUpdate) as any,
        onDelete: (config.mutators?.delete ?? defaultHandlers.onDelete) as any,
        meta: config.meta,
        schema: config.schema,
      }

      const collection = createCollection(queryCollectionOptions(queryConfig))

      return collection as unknown as Collection<TItem, TKey>
    } catch (error) {
      logger.error('Failed to build query collection', {
        table: config.table,
        error,
      })
      throw new Error(`Failed to build query collection for table ${config.table}: ${(error as Error).message}`)
    }
  }
}
